import React, { useEffect, useState } from "react";
import {
  callApiAsync,
  getAddittionalCost,
  postDeleteAddittionalCost,
  getPaymentsByIndependentCost,
} from "../../services/apiService";
import icons from "../../constants/icons";
import {
  actionsColumn,
  hideColumn,
  textColumn,
} from "../tableComponent/columns";
import ButtonComponent from "../button";
import AddAddittionalModal from "./addAddittionalModal";
import PaymentModal from "./paymentModal";
import AlertComponent from "../alertModal";
import TableComponent from "../tableComponent";
import { Chip } from "@mui/material";
import ToastNotification from "../toastNotification";
import {
  CURRENCY_COP,
  CURRENCY_USD,
  formatCurrencyAmount,
} from "../../utils/currencyUtils";

const TableAddittionalCost = ({ billingPeriodId, businessUnitId }) => {
  const [addittionalCostData, setAddittionalCostData] = useState([]);
  const [userAlertOpen, setAlertOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [additionalCost, setAdditionalCost] = useState(null);
  const [selectedIndependentCost, setSelectedIndependentCost] = useState(null);

  const [openAddAddittionalCostModal, setOpenAddAddittionalCostModal] =
    useState(false);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("error");

  const getAddittionalCostDataTable = async () => {
    await callApiAsync(
      () => getAddittionalCost(businessUnitId, billingPeriodId),
      (data) => {
        setAddittionalCostData(data);
      }
    );
  };

  const deleteAddittionalCost = async (data) => {
    const dataToDelete = { id: data?.id };
    try {
      await callApiAsync(
        () => postDeleteAddittionalCost(dataToDelete.id),
        (data) => {
          getAddittionalCostDataTable();
        }
      );
    } catch (error) {
      console.error("Error deleting independent cost:", error);
      setToastMessage("Error al eliminar el costo independiente");
      setToastSeverity("error");
      setToastOpen(true);
    }
  };

  const handleTableDelete = async (row) => {
    try {
      const payments = await callApiAsync(
        () => getPaymentsByIndependentCost(row.id),
        (payments) => {
          if (payments && payments.length > 0) {
            setToastMessage(
              `No se puede eliminar este costo independiente porque tiene ${payments.length} pago(s) registrado(s). Elimina todos los pagos primero.`
            );
            setToastSeverity("error");
            setToastOpen(true);
            return;
          }

          setSelectedRow(row);
          setAlertOpen(true);
        }
      );
    } catch (error) {
      console.error("Error checking payments:", error);
      setToastMessage("Error al verificar los pagos del costo independiente");
      setToastSeverity("error");
      setToastOpen(true);
    }
  };

  const handleDelete = () => {
    deleteAddittionalCost(selectedRow);
    setAlertOpen(false);
    setToastMessage("Costo independiente eliminado exitosamente");
    setToastSeverity("success");
    setToastOpen(true);
  };

  const openModal = (row) => {
    if (row) {
      setAdditionalCost(row);
    }
    setOpenAddAddittionalCostModal(true);
  };

  const openPaymentManagement = (row) => {
    setSelectedIndependentCost(row);
    setOpenPaymentModal(true);
  };

  const isRowCop = (row) => (row.currency ?? CURRENCY_COP) === CURRENCY_COP;
  const isRowUsd = (row) => row.currency === CURRENCY_USD;

  const columns = [
    hideColumn("id", "ID"),
    textColumn("name", "Nombre"),
    textColumn("description", "Descripción"),
    {
      id: "totalCOP",
      header: "Total (COP)",
      size: 120,
      Cell: ({ row }) =>
        isRowCop(row.original)
          ? formatCurrencyAmount(row.original.price || 0, CURRENCY_COP)
          : "-",
      Footer: ({ table }) => {
        const sum = table
          .getRowModel()
          .rows.filter((r) => isRowCop(r.original))
          .reduce((acc, r) => acc + (r.original.price || 0), 0);
        return `Total COP: ${formatCurrencyAmount(sum, CURRENCY_COP)}`;
      },
    },
    {
      id: "totalUSD",
      header: "Total (USD)",
      size: 120,
      Cell: ({ row }) =>
        isRowUsd(row.original)
          ? formatCurrencyAmount(row.original.price || 0, CURRENCY_USD)
          : "-",
      Footer: ({ table }) => {
        const sum = table
          .getRowModel()
          .rows.filter((r) => isRowUsd(r.original))
          .reduce((acc, r) => acc + (r.original.price || 0), 0);
        return `Total USD: ${formatCurrencyAmount(sum, CURRENCY_USD)}`;
      },
    },
    {
      accessorKey: "paymentStatus",
      header: "Estado de Pago",
      Cell: ({ row }) => {
        const isPaid = row.original.isPaid;
        return (
          <Chip
            label={isPaid ? "Pagado" : "Pendiente"}
            color={isPaid ? "success" : "warning"}
            size="small"
          />
        );
      },
    },
    actionsColumn(
      ({ row }) => (
        <div className="HContainer">
          <ButtonComponent
            onClick={() => openModal(row.original)}
            variant="contained"
            backgroundColor="#fff0"
            title="Editar"
          >
            <img
              className="btn-icon w-icon-25"
              src={icons.blue_edit}
              alt="icon"
            />
          </ButtonComponent>
          <ButtonComponent
            onClick={() => openPaymentManagement(row.original)}
            variant="contained"
            backgroundColor="#fff0"
            title="Gestionar Pagos"
          >
            <img
              className="btn-icon w-icon-25"
              src={icons.bill}
              alt="payment"
            />
          </ButtonComponent>
          <ButtonComponent
            onClick={() => handleTableDelete(row.original)}
            variant="contained"
            backgroundColor="#fff0"
            title="Eliminar"
          >
            <img
              className="btn-icon w-icon-25"
              src={icons.red_delete}
              alt="icon"
            />
          </ButtonComponent>
        </div>
      ),
      150
    ),
  ];

  useEffect(() => {
    if (billingPeriodId) {
      getAddittionalCostDataTable(billingPeriodId);
    } else {
      setAddittionalCostData([]);
    }
  }, [billingPeriodId]);

  const handleAddIndependentCost = () => {
    if (billingPeriodId && businessUnitId) {
      openModal();
    } else {
      setToastMessage(
        "No se puede agregar un costo independiente sin un periodo y una unidad de negocio"
      );
      setToastSeverity("warning");
      setToastOpen(true);
    }
  };

  return (
    <div>
      <TableComponent
        columns={columns}
        data={addittionalCostData}
        enableGlobalFilter={true}
        headerButtons={() => (
          <ButtonComponent
            onClick={handleAddIndependentCost}
            type="submit"
            variant="contained"
            backgroundColor="#F6C079"
            textColor="#000000"
            margin="15px 0"
          >
            <div className="HContainer a-center">
              <img
                className="btn-icon icon-margin-r-15"
                src={icons.plus}
                alt="icon"
              />
              <p>Agregar costos independientes</p>
            </div>
          </ButtonComponent>
        )}
      />
      <AddAddittionalModal
        additionalCost={additionalCost}
        setAdditionalCost={setAdditionalCost}
        getAddittionalCostDataTable={getAddittionalCostDataTable}
        open={openAddAddittionalCostModal}
        billingPeriodId={billingPeriodId}
        businessUnitId={businessUnitId}
        onClose={() => {
          setOpenAddAddittionalCostModal(false);
        }}
      />
      <PaymentModal
        independentCost={selectedIndependentCost}
        getAddittionalCostDataTable={getAddittionalCostDataTable}
        open={openPaymentModal}
        onClose={() => {
          setOpenPaymentModal(false);
          setSelectedIndependentCost(null);
        }}
      />
      <AlertComponent
        open={userAlertOpen}
        onClose={() => setAlertOpen(false)}
        onConfirm={handleDelete}
        title="Confirmar Eliminación"
        message="¿Estás seguro de que deseas eliminar este costo independiente?"
      />

      <ToastNotification
        open={toastOpen}
        handleClose={() => setToastOpen(false)}
        message={toastMessage}
        severity={toastSeverity}
      />
    </div>
  );
};

export default TableAddittionalCost;
